import type { Country, GeoData } from "../game/geo.ts";
import { h } from "./dom.ts";

/** An ARIA combobox: type, arrow through up to 4 suggestions, Return guesses. */
export class GuessField {
  readonly element: HTMLElement;
  readonly input: HTMLInputElement;
  private readonly list: HTMLUListElement;
  private readonly geo: GeoData;
  private readonly isGuessed: (id: string) => boolean;
  private readonly onChange: () => void;
  private readonly onSubmit: (country: Country | undefined) => void;
  private options: Country[] = [];
  private highlighted = -1;
  /** A picked suggestion or globe tap; `fromTap` stops the globe turning under the player's finger. */
  picked: (Country & { fromTap?: boolean }) | undefined;

  constructor(
    geo: GeoData,
    isGuessed: (id: string) => boolean,
    onChange: () => void,
    onSubmit: (country: Country | undefined) => void,
  ) {
    this.geo = geo;
    this.isGuessed = isGuessed;
    this.onChange = onChange;
    this.onSubmit = onSubmit;
    this.input = h("input", {
      class: "field", type: "text", role: "combobox", "aria-autocomplete": "list", "aria-expanded": "false",
      "aria-controls": "suggestions", "aria-label": "Type a country", placeholder: "Type a country", autocomplete: "off",
      autocapitalize: "words", spellcheck: "false",
    });
    this.list = h("ul", { id: "suggestions", role: "listbox", class: "suggestions", hidden: true });
    this.element = h("div", { class: "combo" }, this.list, this.input);
    this.input.addEventListener("input", () => { this.picked = undefined; this.refresh(); this.onChange(); });
    this.input.addEventListener("keydown", (e) => this.key(e));
  }

  /** The country Guess would submit: a picked suggestion, or an exact name or alias. */
  get selected(): Country | undefined {
    return this.picked ?? this.geo.match(this.input.value);
  }

  pick(country: Country, fromTap = false): void {
    if (this.isGuessed(country.id)) return;
    this.input.value = country.name;
    this.picked = { ...country, fromTap };
    this.close();
    this.onChange();
  }

  clear(): void {
    this.input.value = "";
    this.picked = undefined;
    this.close();
    this.onChange();
  }

  private key(e: KeyboardEvent): void {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (this.options.length === 0) return;
      e.preventDefault();
      const next = this.highlighted + (e.key === "ArrowDown" ? 1 : -1);
      this.highlighted = Math.max(0, Math.min(this.options.length - 1, next));
      this.render();
    } else if (e.key === "Enter") {
      e.preventDefault();
      // A highlighted suggestion wins, even an already-guessed one, so play can say so.
      this.onSubmit(this.options[this.highlighted] ?? this.selected);
    } else if (e.key === "Escape") {
      this.close();
    }
  }

  private refresh(): void {
    this.options = this.picked ? [] : this.geo.suggestions(this.input.value, 4);
    this.highlighted = -1;
    this.render();
  }

  private close(): void {
    this.options = [];
    this.highlighted = -1;
    this.render();
  }

  private render(): void {
    const open = this.options.length > 0;
    this.list.hidden = !open;
    this.input.setAttribute("aria-expanded", String(open));
    this.list.replaceChildren(...this.options.map((c, i) => {
      const guessed = this.isGuessed(c.id);
      const li = h("li", {
        role: "option", id: `option-${i}`, "aria-selected": String(i === this.highlighted),
        "aria-disabled": guessed ? "true" : undefined, class: i === this.highlighted ? "highlighted" : undefined,
      }, c.name, guessed ? h("span", { class: "muted" }, " already guessed") : null);
      li.addEventListener("mousedown", (e) => { e.preventDefault(); this.pick(c); });
      return li;
    }));
    if (this.highlighted >= 0) this.input.setAttribute("aria-activedescendant", `option-${this.highlighted}`);
    else this.input.removeAttribute("aria-activedescendant");
  }
}
